/**
 * <park-admin-status> — the operator action-result banner (anton/forrest#1021),
 * lifted 1:1 from `.gm-status` / `.gm-addstatus` in
 * `components/admin/GalleryManager.astro` (anton/forrest#674/anton/forrest#773).
 *
 * `admin-shell#FR-9` mandates this pattern across every admin surface: "surface
 * every failed operator action as a visible, localised banner". GalleryManager
 * is currently its only user (per-card reorder/caption save status, and the
 * toolbar upload status), but the componentization exists so the tone mapping
 * — `data-kind` in `{saving|saved|error}` → muted/primary/danger colour — can
 * never again be added to one call site and forgotten on another (the fix for
 * `anton/forrest#1021` DEFECT 2: `.gm-addstatus` previously carried NO tone at
 * all, so a failed upload looked identical to "uploading…").
 *
 * Unlike `<park-admin-tile-grid>`, this element's content is updated
 * IMPERATIVELY by the page's own client script (`setStatusEl()` in
 * GalleryManager) — `kind`/`text` are not re-derived from attributes on every
 * interaction, they're written straight onto the live DOM node exactly as the
 * old bare `<span>` was. The `renderParkAdminStatus()` pure fn below only
 * covers the two contexts that DO go through attributes: the initial SSR
 * render (empty, no kind yet) and the Claude-Design author-syntax preview
 * (`kind`/`text` attributes, for judging the three tones side by side).
 *
 * Author syntax:
 *   <park-admin-status kind="saved" text="Tallennettu"></park-admin-status>
 *
 * Styling lives in wc/css/park-admin-status.css — no <style> here, no shadow
 * DOM. `variant="toolbar"` (a plain class modifier, not a separate element)
 * covers the `.gm-addstatus` left-aligned/12px case; the default variant
 * covers the per-card `.gm-status` right-aligned/11.5px case.
 *
 * @dsSpec admin-shell#FR-9 — the operator action-result banner.
 *   (Traceability: specs/design-system/traceability.md; the generated card
 *   carries this same ref in its @dsCard marker — anton/forrest#846.)
 */

import { ParkElement, defineElement, esc } from "./base";

const TAG = "park-admin-status";

export type ParkAdminStatusKind = "saving" | "saved" | "error" | "";

export interface ParkAdminStatusData {
	kind?: ParkAdminStatusKind;
	text?: string;
}

/**
 * Pure render: (kind, text) → inner HTML string (just the escaped text — the
 * `data-kind` tone is a DOM attribute on the element itself, set in
 * `afterRender()`/the SSR wrapper, not part of the inner markup). Safe to call
 * server-side and client-side.
 */
export function renderParkAdminStatus(data: ParkAdminStatusData): string {
	return esc(data.text ?? "");
}

class ParkAdminStatus extends ParkElement {
	static get observedAttributes(): string[] {
		return ["kind", "text"];
	}

	protected render(): string {
		return renderParkAdminStatus({
			kind: (this.attr("kind") as ParkAdminStatusKind) || "",
			text: this.attr("text"),
		});
	}

	protected afterRender(): void {
		const kind = this.attr("kind");
		if (kind) this.setAttribute("data-kind", kind);
		else this.removeAttribute("data-kind");
	}
}

defineElement(TAG, ParkAdminStatus);

export { ParkAdminStatus };
